import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Boxes, LayoutGrid, MapPin, PackageSearch, Plus, Pencil, Trash2, Unlink, UserCheck, Vault } from "lucide-react";
import * as magazzinoApi from "../../api/magazzino";
import {
  OrdineMagazzino,
  AccessorioMagazzino,
  RigaDisponibilita,
  CassaforteMagazzino,
  FiltroSedeCasseforti,
  STATO_ORDINE_LABELS,
  STATO_MATCH_LABELS,
  TIPO_ACCESSORIO_LABELS,
  UBICAZIONE_LABELS,
  UBICAZIONI_DISPONIBILI,
} from "../../api/magazzino";
import { getErrorMessage } from "../../api/client";
import { useToast } from "../../context/ToastContext";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { useAuth } from "../../context/AuthContext";
import { OrdineForm } from "./OrdineForm";
import { AccessorioForm } from "./AccessorioForm";
import { AssegnaMatricola } from "./AssegnaMatricola";
import { CambiaUbicazione } from "./CambiaUbicazione";
import { RisolviCliente } from "./RisolviCliente";
import { CassaforteForm } from "./CassaforteForm";
import { ModificaCassaforte } from "./ModificaCassaforte";
import { etichettaMatricola } from "../../utils/matricola";

/**
 * Magazzino — Fase 4, punto 8.
 *
 * Le validazioni che il database non può esprimere restano tutte nel backend —
 * l'intervallo di matricole sui soli sacchi, il fornitore che dev'essere
 * davvero un fornitore, un ordine confermato che vuole un cliente esistente. I
 * form le ANTICIPANO dove aiuta a compilare (un campo nascosto, un'opzione
 * disabilitata), ma non le riscrivono: il controllo vero è là, perché là
 * passano anche le chiamate che non vengono da questa pagina.
 *
 * La **matricola** non è un campo di nessuno dei due form: è l'identità del
 * pezzo fisico e si assegna con un'azione a sé sulla riga dell'ordine, quando
 * il fornitore la comunica. Vedi `AssegnaMatricola.tsx`.
 */
function dataIT(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString("it-IT") : "—";
}

function coloreMatch(stato: magazzinoApi.StatoMatchCliente): "success" | "warning" | "neutral" {
  // Agganciato dal sistema e assegnato a mano sono entrambi "risolto": stesso
  // colore, etichette diverse. Il giallo resta a chi aspetta una decisione.
  if (stato === "automatico" || stato === "risolto_manualmente") return "success";
  if (stato === "da_confermare") return "warning";
  return "neutral";
}

export default function Magazzino() {
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const [ordini, setOrdini] = useState<OrdineMagazzino[]>([]);
  const [accessori, setAccessori] = useState<AccessorioMagazzino[]>([]);
  const [disponibilita, setDisponibilita] = useState<{
    righe: RigaDisponibilita[];
    totali: { disponibili: number; fuori: number };
  } | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const puoCreare = hasPermission("magazzino:create");
  const puoModificare = hasPermission("magazzino:update");
  const puoEliminare = hasPermission("magazzino:delete");
  // Sede, consegna e matricola si scrivono con PATCH /api/devices/:id: serve
  // il permesso di quella rotta, non quello del magazzino.
  const puoModificareCasseforti = hasPermission("devices:update");

  // Le casseforti si caricano a parte dal resto: il filtro sulla sede le
  // ricarica da sole, senza rifare ordini, accessori e disponibilità.
  const [casseforti, setCasseforti] = useState<CassaforteMagazzino[]>([]);
  const [filtroSede, setFiltroSede] = useState<FiltroSedeCasseforti>("");
  const [cassefortiCaricate, setCassefortiCaricate] = useState(false);

  /**
   * Un solo pannello per volta, e ognuno con il suo record: tenerli in stati
   * separati renderebbe possibile aprirne due insieme con un errore di una riga.
   * `null` = nessun pannello aperto.
   */
  const [pannello, setPannello] = useState<
    | { tipo: "ordine"; record?: OrdineMagazzino }
    | { tipo: "accessorio"; record?: AccessorioMagazzino }
    | { tipo: "matricola"; record: OrdineMagazzino }
    | { tipo: "ubicazione"; record: OrdineMagazzino }
    | { tipo: "risolvi"; record: OrdineMagazzino }
    | { tipo: "cassaforte" }
    | { tipo: "modifica_cassaforte"; record: CassaforteMagazzino }
    | null
  >(null);

  const carica = useCallback(async () => {
    setIsLoading(true);
    try {
      const [o, a, d] = await Promise.all([
        magazzinoApi.listOrdini(),
        magazzinoApi.listAccessori(),
        magazzinoApi.getDisponibilita(),
      ]);
      setOrdini(o.records);
      setAccessori(a.records);
      setDisponibilita(d);
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile caricare il magazzino."), "error");
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void carica();
  }, [carica]);

  const caricaCasseforti = useCallback(async () => {
    try {
      const { records } = await magazzinoApi.listCasseforti(filtroSede);
      setCasseforti(records);
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile caricare le casseforti in magazzino."), "error");
    } finally {
      setCassefortiCaricate(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroSede]);

  useEffect(() => {
    void caricaCasseforti();
  }, [caricaCasseforti]);

  async function eliminaOrdine(o: OrdineMagazzino) {
    // Il device eventualmente collegato NON viene toccato dal backend: è un
    // pezzo fisico che esiste, la riga d'ordine che lo ha portato è un'altra
    // cosa. Vale la pena dirlo qui, perché è la domanda che uno si fa.
    const conferma = window.confirm(
      `Eliminare l'ordine ${o.odoo_order_ref ?? "senza riferimento"}?\n\n` +
        (o.unita.length > 0
          ? `${o.unita.length === 1 ? "Il dispositivo" : "I dispositivi"} ${o.unita
              .map((u) => etichettaMatricola(u.device.matricola))
              .join(", ")} ${o.unita.length === 1 ? "resta censito" : "restano censiti"}: sparisce solo la riga d'ordine.`
          : "L'operazione non è reversibile.")
    );
    if (!conferma) return;
    try {
      await magazzinoApi.eliminaOrdine(o.id);
      showToast("Ordine eliminato.", "success");
      void carica();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile eliminare l'ordine."), "error");
    }
  }

  async function scollega(o: OrdineMagazzino, unitaId: string, matricola: string) {
    // Si toglie il COLLEGAMENTO, non il pezzo: vale la pena dirlo, perché è
    // esattamente la paura di chi preme.
    const conferma = window.confirm(
      `Scollegare la matricola ${matricola} dall'ordine ${o.odoo_order_ref ?? "senza riferimento"}?\n\n` +
        "Il dispositivo resta censito con tutto il suo storico: sparisce solo il collegamento con questo ordine."
    );
    if (!conferma) return;
    try {
      await magazzinoApi.scollegaUnita(o.id, unitaId);
      showToast(`Matricola ${matricola} scollegata.`, "success");
      void carica();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile scollegare la matricola."), "error");
    }
  }

  async function eliminaAccessorio(a: AccessorioMagazzino) {
    const conferma = window.confirm(
      `Eliminare la riga «${TIPO_ACCESSORIO_LABELS[a.tipo_accessorio]}» (${a.quantita_disponibile} pezzi)?\n\n` +
        "L'operazione non è reversibile."
    );
    if (!conferma) return;
    try {
      await magazzinoApi.eliminaAccessorio(a.id);
      showToast("Accessorio eliminato.", "success");
      void carica();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile eliminare l'accessorio."), "error");
    }
  }

  if (isLoading) {
    return <p className="text-sm text-ink/50">Caricamento…</p>;
  }

  return (
    <div>
      <header className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-ink">Magazzino</h1>
          <p className="mt-1 text-sm text-ink/50">
            Le casseforti a scaffale, che cosa è stato ordinato ai fornitori, e quanto materiale di consumo c&apos;è
            adesso.
          </p>
        </div>
        {puoCreare && (
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={() => setPannello({ tipo: "accessorio" })}>
              <Plus className="h-4 w-4" aria-hidden="true" />
              Accessorio
            </Button>
            <Button onClick={() => setPannello({ tipo: "ordine" })}>
              <Plus className="h-4 w-4" aria-hidden="true" />
              Nuovo ordine
            </Button>
          </div>
        )}
      </header>

      {/* --- Disponibilità ----------------------------------------------------
          Prima di tutto il resto: la domanda con cui si arriva su questa
          pagina è "quanti ne ho", non "quali ordini ho fatto". */}
      {disponibilita && disponibilita.righe.length > 0 && (
        <section className="mb-8">
          <div className="mb-2 flex items-center gap-2">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <LayoutGrid className="h-4 w-4" aria-hidden="true" />
            </span>
            <h2 className="text-sm font-bold uppercase tracking-wide text-ink/70">Disponibilità</h2>
            <span className="text-sm text-ink/40">
              {disponibilita.totali.disponibili} {disponibilita.totali.disponibili === 1 ? "pezzo" : "pezzi"}
              {disponibilita.totali.fuori > 0 && ` · ${disponibilita.totali.fuori} dal cliente`}
            </span>
          </div>

          <div className="overflow-x-auto rounded-card border border-line bg-white shadow-card">
            <table className="w-full min-w-[700px] text-sm">
              <thead className="border-b border-line text-left text-xs uppercase tracking-wide text-ink/50">
                <tr>
                  <th className="px-4 py-3 font-semibold">Modello</th>
                  {UBICAZIONI_DISPONIBILI.map((u) => (
                    <th key={u} className="px-4 py-3 text-right font-semibold">
                      {UBICAZIONE_LABELS[u]}
                    </th>
                  ))}
                  <th className="px-4 py-3 text-right font-semibold">Disponibili</th>
                  {/* Staccata dalle altre da una linea: non è disponibilità,
                      è merce uscita, e sommarla direbbe di avere pezzi che non
                      si hanno. */}
                  <th className="border-l border-line px-4 py-3 text-right font-semibold text-ink/40">Dal cliente</th>
                </tr>
              </thead>
              <tbody>
                {disponibilita.righe.map((r) => (
                  <tr key={r.dispositivo_catalogo_id ?? "senza-modello"} className="border-b border-line last:border-b-0">
                    <td className="px-4 py-3 text-ink">
                      {r.etichetta}
                      {r.sigla && <span className="ml-1 text-xs text-ink/45">({r.sigla})</span>}
                    </td>
                    {UBICAZIONI_DISPONIBILI.map((u) => (
                      <td key={u} className="px-4 py-3 text-right tabular-nums text-ink/70">
                        {r.quantita[u] || <span className="text-ink/25">—</span>}
                      </td>
                    ))}
                    <td className="px-4 py-3 text-right font-semibold tabular-nums text-ink">{r.disponibili}</td>
                    <td className="border-l border-line px-4 py-3 text-right tabular-nums text-ink/40">
                      {r.fuori || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* --- Casseforti in magazzino -------------------------------------------
          Pezzi fisici, non ordini. Quali siano «in magazzino» lo decide il
          backend (caricate da qui e senza installazione collegata): appena
          un'installazione nomina la matricola, la riga sparisce da sola. */}
      <section className="mb-8">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Vault className="h-4 w-4" aria-hidden="true" />
          </span>
          <h2 className="text-sm font-bold uppercase tracking-wide text-ink/70">Casseforti in magazzino</h2>
          <span className="text-sm text-ink/40">{casseforti.length}</span>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <label htmlFor="filtro-sede-casseforti" className="text-xs font-medium text-ink/60">
              Sede
            </label>
            <select
              id="filtro-sede-casseforti"
              className="h-8 rounded-lg border border-line bg-white px-2 text-sm text-ink"
              value={filtroSede}
              onChange={(e) => setFiltroSede(e.target.value as FiltroSedeCasseforti)}
            >
              <option value="">Tutte</option>
              <option value="senza">Senza sede</option>
              <option value="con">Con sede</option>
            </select>
            {puoCreare && (
              <Button size="sm" onClick={() => setPannello({ tipo: "cassaforte" })}>
                <Plus className="h-4 w-4" aria-hidden="true" />
                Aggiungi cassaforte
              </Button>
            )}
          </div>
        </div>

        {!cassefortiCaricate ? (
          <p className="text-sm text-ink/50">Caricamento…</p>
        ) : casseforti.length === 0 ? (
          <p className="rounded-card border border-dashed border-line px-4 py-6 text-center text-sm text-ink/45">
            {filtroSede === "con"
              ? "Nessuna cassaforte in magazzino con una sede assegnata."
              : filtroSede === "senza"
                ? "Nessuna cassaforte in magazzino senza sede."
                : "Nessuna cassaforte in magazzino."}
          </p>
        ) : (
          <div className="overflow-x-auto rounded-card border border-line bg-white shadow-card">
            <table className="w-full min-w-[800px] text-sm">
              <thead className="border-b border-line text-left text-xs uppercase tracking-wide text-ink/50">
                <tr>
                  <th className="px-4 py-3 font-semibold">Marca e modello</th>
                  <th className="px-4 py-3 font-semibold">Matricola</th>
                  <th className="px-4 py-3 font-semibold">Sede</th>
                  <th className="px-4 py-3 font-semibold">Consegna prevista</th>
                  <th className="px-4 py-3 font-semibold">Caricata il</th>
                  <th className="px-4 py-3">
                    <span className="sr-only">Azioni</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {casseforti.map((c) => (
                  <tr key={c.id} className="border-b border-line last:border-b-0">
                    <td className="px-4 py-3 text-ink">
                      {c.dispositivo_catalogo
                        ? `${c.dispositivo_catalogo.marca} ${c.dispositivo_catalogo.modello}`
                        : "Modello non indicato"}
                      {c.dispositivo_catalogo?.sigla && (
                        <span className="ml-1 text-xs text-ink/45">({c.dispositivo_catalogo.sigla})</span>
                      )}
                    </td>
                    <td className={c.matricola ? "px-4 py-3 font-semibold text-ink" : "px-4 py-3 italic text-ink/45"}>
                      {etichettaMatricola(c.matricola)}
                    </td>
                    <td className="px-4 py-3">
                      {c.sede_cliente ? (
                        <>
                          <span className="text-ink">
                            {[c.sede_cliente.nome_sede, c.sede_cliente.citta].filter(Boolean).join(" · ") || "Sede"}
                          </span>
                          <span className="block text-xs text-ink/45">{c.sede_cliente.cliente.nome}</span>
                        </>
                      ) : (
                        <span className="text-ink/40">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 tabular-nums text-ink/70">{dataIT(c.data_consegna_prevista)}</td>
                    <td className="px-4 py-3 tabular-nums text-ink/50">{dataIT(c.caricata_in_magazzino_il)}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        {puoModificareCasseforti && (
                          <button
                            type="button"
                            onClick={() => setPannello({ tipo: "modifica_cassaforte", record: c })}
                            title="Sede, consegna prevista e matricola"
                            aria-label={`Modifica la cassaforte ${etichettaMatricola(c.matricola)}`}
                            className="rounded-lg p-1.5 text-ink/50 transition-colors hover:bg-primary/5 hover:text-primary"
                          >
                            <MapPin className="h-4 w-4" aria-hidden="true" />
                          </button>
                        )}
                        <Link
                          to={`/devices/${c.id}`}
                          className="rounded-lg px-2 py-1.5 text-xs font-medium text-primary hover:bg-primary/5"
                        >
                          Scheda
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* --- Ordini device ---------------------------------------------------- */}
      <section className="mb-8">
        <div className="mb-2 flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <PackageSearch className="h-4 w-4" aria-hidden="true" />
          </span>
          <h2 className="text-sm font-bold uppercase tracking-wide text-ink/70">Ordini dispositivi</h2>
          <span className="text-sm text-ink/40">{ordini.length}</span>
        </div>

        {ordini.length === 0 ? (
          <p className="rounded-card border border-dashed border-line px-4 py-6 text-center text-sm text-ink/45">
            Nessun ordine registrato.
          </p>
        ) : (
          <div className="overflow-x-auto rounded-card border border-line bg-white shadow-card">
            <table className="w-full min-w-[900px] text-sm">
              <thead className="border-b border-line text-left text-xs uppercase tracking-wide text-ink/50">
                <tr>
                  <th className="px-4 py-3 font-semibold">Ordine</th>
                  <th className="px-4 py-3 font-semibold">Data</th>
                  <th className="px-4 py-3 font-semibold">Modello</th>
                  <th className="px-4 py-3 font-semibold">Qtà</th>
                  <th className="px-4 py-3 font-semibold">Stato</th>
                  <th className="px-4 py-3 font-semibold">Cliente</th>
                  <th className="px-4 py-3 font-semibold">Dove</th>
                  <th className="px-4 py-3 font-semibold">Unità arrivate</th>
                  {(puoModificare || puoEliminare) && <th className="px-4 py-3 font-semibold text-right">Azioni</th>}
                </tr>
              </thead>
              <tbody>
                {ordini.map((o) => (
                  <tr key={o.id} className="border-b border-line last:border-b-0">
                    <td className="px-4 py-3 text-ink">{o.odoo_order_ref ?? "—"}</td>
                    <td className="px-4 py-3 text-ink/70">{dataIT(o.data_ordine)}</td>
                    <td className="px-4 py-3 text-ink/70">
                      {o.dispositivo_catalogo
                        ? `${o.dispositivo_catalogo.marca} ${o.dispositivo_catalogo.modello}`
                        : "—"}
                    </td>
                    <td className="px-4 py-3 text-ink">{o.quantita}</td>
                    <td className="px-4 py-3">
                      <Badge color={o.stato_ordine === "confermato" ? "success" : "neutral"}>
                        {STATO_ORDINE_LABELS[o.stato_ordine]}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      {/* Il nome dell'anagrafica quando il match è riuscito, altrimenti
                          quello scritto a mano — con accanto lo stato, perché
                          "da confermare" è un lavoro da fare, non un dettaglio. */}
                      <div className="text-ink">{o.cliente_preassegnato?.nome ?? o.cliente_nuovo_nome ?? "—"}</div>
                      <div className="mt-0.5 flex items-center gap-1.5">
                        <Badge color={coloreMatch(o.stato_match_cliente)}>
                          {STATO_MATCH_LABELS[o.stato_match_cliente]}
                        </Badge>
                        {/* Solo su chi aspetta una decisione: sugli altri non
                            c'è niente da risolvere, e il pulsante sarebbe un
                            invito a cambiare una scelta già presa. */}
                        {puoModificare && o.stato_match_cliente === "da_confermare" && (
                          <button
                            type="button"
                            onClick={() => setPannello({ tipo: "risolvi", record: o })}
                            title="Scegli il cliente"
                            aria-label="Scegli il cliente"
                            className="rounded-lg p-1 text-ink/45 transition-colors hover:bg-primary/10 hover:text-primary"
                          >
                            <UserCheck className="h-4 w-4" aria-hidden="true" />
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {/* `presso_cliente` in grigio: è merce uscita, non
                          disponibilità, e non deve leggersi come le altre tre. */}
                      <span className={o.ubicazione === "presso_cliente" ? "text-ink/40" : "text-ink/70"}>
                        {UBICAZIONE_LABELS[o.ubicazione]}
                      </span>
                      {/* Il posto preciso sotto il tipo di posto: senza, la
                          colonna direbbe "presso il fornitore" e uno dovrebbe
                          aprire il pannello per sapere quale. */}
                      {(o.centro_assistenza || o.fornitore || o.sede_cliente) && (
                        <div className="text-xs text-ink/45">
                          {o.centro_assistenza?.nome ??
                            o.fornitore?.nome ??
                            [o.sede_cliente?.nome_sede, o.sede_cliente?.citta].filter(Boolean).join(" · ")}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-ink/70">
                      {/* Il conteggio prima delle matricole: la domanda che si
                          fa chi guarda è "è arrivato tutto?", non "quali sono".
                          Su un ordine da un pezzo il rapporto è muto e si
                          legge solo la matricola. */}
                      {o.unita.length === 0 ? (
                        <span className="text-ink/40">nessuna{o.quantita > 1 ? ` di ${o.quantita}` : ""}</span>
                      ) : (
                        <div className="flex flex-col gap-0.5">
                          {o.quantita > 1 && (
                            <span className={o.unita.length === o.quantita ? "text-ink/60" : "font-semibold text-ink"}>
                              {o.unita.length} di {o.quantita}
                            </span>
                          )}
                          {o.unita.map((u) => (
                            <span key={u.id} className="flex items-center gap-1.5">
                              {etichettaMatricola(u.device.matricola)}
                              {puoModificare && (
                                <button
                                  type="button"
                                  onClick={() => void scollega(o, u.id, etichettaMatricola(u.device.matricola))}
                                  title={`Scollega ${etichettaMatricola(u.device.matricola)} da questo ordine`}
                                  aria-label={`Scollega ${etichettaMatricola(u.device.matricola)} da questo ordine`}
                                  className="rounded p-0.5 text-ink/30 transition-colors hover:bg-accent/10 hover:text-accent"
                                >
                                  <Unlink className="h-3.5 w-3.5" aria-hidden="true" />
                                </button>
                              )}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    {(puoModificare || puoEliminare) && (
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {/* Finché c'è posto: un ordine da 5 accetta 5
                              matricole, che arrivano in momenti diversi. Quando
                              è pieno il pulsante sparisce, e per correggere si
                              scollega prima. */}
                          {puoModificare && o.unita.length < o.quantita && (
                            <Button variant="ghost" size="sm" onClick={() => setPannello({ tipo: "matricola", record: o })}>
                              <Vault className="h-4 w-4" aria-hidden="true" />
                              Assegna matricola
                            </Button>
                          )}
                          {puoModificare && (
                            <button
                              type="button"
                              onClick={() => setPannello({ tipo: "ubicazione", record: o })}
                              title="Sposta la merce"
                              aria-label="Sposta la merce"
                              className="rounded-lg p-1.5 text-ink/45 transition-colors hover:bg-primary/10 hover:text-primary"
                            >
                              <MapPin className="h-4 w-4" aria-hidden="true" />
                            </button>
                          )}
                          {puoModificare && (
                            <button
                              type="button"
                              onClick={() => setPannello({ tipo: "ordine", record: o })}
                              title="Modifica l'ordine"
                              aria-label="Modifica l'ordine"
                              className="rounded-lg p-1.5 text-ink/45 transition-colors hover:bg-primary/10 hover:text-primary"
                            >
                              <Pencil className="h-4 w-4" aria-hidden="true" />
                            </button>
                          )}
                          {puoEliminare && (
                            <button
                              type="button"
                              onClick={() => void eliminaOrdine(o)}
                              title="Elimina l'ordine"
                              aria-label="Elimina l'ordine"
                              className="rounded-lg p-1.5 text-ink/35 transition-colors hover:bg-accent/10 hover:text-accent"
                            >
                              <Trash2 className="h-4 w-4" aria-hidden="true" />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* --- Accessori -------------------------------------------------------- */}
      <section>
        <div className="mb-2 flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Boxes className="h-4 w-4" aria-hidden="true" />
          </span>
          <h2 className="text-sm font-bold uppercase tracking-wide text-ink/70">Accessori</h2>
          <span className="text-sm text-ink/40">{accessori.length}</span>
        </div>

        {accessori.length === 0 ? (
          <p className="rounded-card border border-dashed border-line px-4 py-6 text-center text-sm text-ink/45">
            Nessun accessorio a magazzino.
          </p>
        ) : (
          <div className="overflow-x-auto rounded-card border border-line bg-white shadow-card">
            <table className="w-full min-w-[700px] text-sm">
              <thead className="border-b border-line text-left text-xs uppercase tracking-wide text-ink/50">
                <tr>
                  <th className="px-4 py-3 font-semibold">Tipo</th>
                  <th className="px-4 py-3 font-semibold">Disponibili</th>
                  <th className="px-4 py-3 font-semibold">Lotto (matricole)</th>
                  <th className="px-4 py-3 font-semibold">Fornitore</th>
                  <th className="px-4 py-3 font-semibold">Note</th>
                  {(puoModificare || puoEliminare) && <th className="px-4 py-3 font-semibold text-right">Azioni</th>}
                </tr>
              </thead>
              <tbody>
                {accessori.map((a) => (
                  <tr key={a.id} className="border-b border-line last:border-b-0">
                    <td className="px-4 py-3 text-ink">{TIPO_ACCESSORIO_LABELS[a.tipo_accessorio]}</td>
                    <td className="px-4 py-3 text-ink">{a.quantita_disponibile}</td>
                    {/* Solo i sacchi possono averlo: sugli altri la cella resta
                        vuota, non "0" — non è una quantità, è un intervallo. */}
                    <td className="px-4 py-3 text-ink/70">
                      {a.matricola_da && a.matricola_a ? `${a.matricola_da} → ${a.matricola_a}` : "—"}
                    </td>
                    <td className="px-4 py-3 text-ink/70">{a.fornitore?.nome ?? "—"}</td>
                    <td className="px-4 py-3 text-ink/50">{a.note ?? "—"}</td>
                    {(puoModificare || puoEliminare) && (
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {puoModificare && (
                            <button
                              type="button"
                              onClick={() => setPannello({ tipo: "accessorio", record: a })}
                              title="Modifica l'accessorio"
                              aria-label="Modifica l'accessorio"
                              className="rounded-lg p-1.5 text-ink/45 transition-colors hover:bg-primary/10 hover:text-primary"
                            >
                              <Pencil className="h-4 w-4" aria-hidden="true" />
                            </button>
                          )}
                          {puoEliminare && (
                            <button
                              type="button"
                              onClick={() => void eliminaAccessorio(a)}
                              title="Elimina l'accessorio"
                              aria-label="Elimina l'accessorio"
                              className="rounded-lg p-1.5 text-ink/35 transition-colors hover:bg-accent/10 hover:text-accent"
                            >
                              <Trash2 className="h-4 w-4" aria-hidden="true" />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Un pannello per volta. La `key` sul record lo rimonta quando si passa
          da una riga all'altra: i form tengono i valori in stato locale
          inizializzato una volta sola, e senza rimontarli si riaprirebbero coi
          dati della riga precedente. */}
      {pannello?.tipo === "ordine" && (
        <OrdineForm
          key={pannello.record?.id ?? "nuovo"}
          ordine={pannello.record}
          onChiudi={() => setPannello(null)}
          onSalvato={() => void carica()}
        />
      )}

      {pannello?.tipo === "accessorio" && (
        <AccessorioForm
          key={pannello.record?.id ?? "nuovo"}
          accessorio={pannello.record}
          onChiudi={() => setPannello(null)}
          onSalvato={() => void carica()}
        />
      )}

      {pannello?.tipo === "risolvi" && (
        <RisolviCliente
          key={pannello.record.id}
          ordine={pannello.record}
          onChiudi={() => setPannello(null)}
          onFatto={() => void carica()}
        />
      )}

      {pannello?.tipo === "ubicazione" && (
        <CambiaUbicazione
          key={pannello.record.id}
          ordine={pannello.record}
          onChiudi={() => setPannello(null)}
          onFatto={() => void carica()}
        />
      )}

      {pannello?.tipo === "cassaforte" && (
        <CassaforteForm
          onChiudi={() => setPannello(null)}
          onFatto={() => {
            setPannello(null);
            void caricaCasseforti();
          }}
        />
      )}

      {pannello?.tipo === "modifica_cassaforte" && (
        <ModificaCassaforte
          key={pannello.record.id}
          cassaforte={pannello.record}
          onChiudi={() => setPannello(null)}
          onFatto={() => {
            setPannello(null);
            void caricaCasseforti();
          }}
        />
      )}

      {pannello?.tipo === "matricola" && (
        <AssegnaMatricola
          key={pannello.record.id}
          ordine={pannello.record}
          onChiudi={() => setPannello(null)}
          onFatto={() => void carica()}
        />
      )}
    </div>
  );
}
